// Pure helpers for working with cadastre parcel features returned by the
// Google Maps Data layer + state-by-state WFS feeds. Extracted out of
// MapView.js to keep the parent component focused on map / state
// orchestration. No React, no side effects — easy to unit-test.

// Best-effort stable identifier for a Google Maps Data feature. We try the
// configured feature ID first (set via addGeoJson({ idPropertyName })) and
// fall back to scanning common ArcGIS / WFS object-id property names in
// case the server uses non-default casing.
export function getStableFeatureId(feature) {
  if (!feature) return null;
  const direct = feature.getId();
  if (direct !== undefined && direct !== null) return direct;
  const candidates = [
    'OBJECTID', 'objectid', 'OBJECTID_1',
    'cadid', 'CADID', 'FID', 'fid', 'gml_id',
    'parcel_pfi', 'parcel_id', 'pfi',  // VIC
    'PID', 'CID', 'pid', 'cid',         // TAS
    'assno_tenseqno', 'assno',          // SA ValCad
  ];
  for (const k of candidates) {
    const v = feature.getProperty(k);
    if (v !== undefined && v !== null) return v;
  }
  return null;
}

// Map per-state cadastre attribute shapes onto a normalized PropertyInfoPanel schema.
// Different state cadastre endpoints return wildly different field names (e.g. NSW
// uses `LOTNUMBER` / `PLANLABEL`, QLD uses `lot` / `plan` / `lotplan`). The panel
// only cares about a small consistent set; this function does the mapping in one
// place so the panel stays dumb.
export function normalizeParcelDetails(attrs, state, addressComponents) {
  const map = {};
  Object.keys(attrs || {}).forEach((k) => { map[k.toLowerCase()] = attrs[k]; });
  const get = (...keys) => {
    for (const k of keys) {
      const v = map[k.toLowerCase()];
      if (v !== null && v !== undefined && v !== '') return v;
    }
    return null;
  };
  const lot = get(
    'lot', 'lotnumber',
    'parcel_lot_number', 'parcel_allotment', // VIC
    'lpi', 'folio',                            // TAS
  );
  const plan = get(
    'plan', 'planlabel', 'plannumber',
    'parcel_plan_number', 'parcel_spi',        // VIC
    'volume',                                  // TAS (Volume)
  );
  let planNumber = null;
  if (lot && plan) planNumber = `${lot} ${plan}`;
  else planNumber = plan || lot || null;
  let locality = null;
  if (Array.isArray(addressComponents)) {
    const c = addressComponents.find((c) => c.types?.includes('locality'))
      || addressComponents.find((c) => c.types?.includes('sublocality'));
    if (c) locality = c.long_name;
  }
  if (!locality) {
    locality = get(
      'localitygazettedname', 'locality', 'suburb', 'lga_name',
      'add_locality_name', // VIC v_property_mp_address
    );
  }
  return {
    parcelStatus: get(
      'status', 'parcelstatus', 'parcel_status', 'tenure',
      'parcel_status', 'parcel_crown_status', // VIC
      'tenure_ty',                            // TAS
    ),
    parcelType: get(
      'parceltype', 'parcel_type', 'cadastretype', 'cadtype',
      'cad_type1', 'cad_type2',  // TAS
    ) || (lot ? 'Lot' : null),
    planNumber,
    lotNumber: lot,
    locality,
    state: state || null,
  };
}
